import { normalizeBlockTitle } from '../model/block-title';
import type { BlockStatus } from '../model/enums';
import type { IdSpan } from './span';

/** What `mergeForDisplay` needs from a block. */
export interface MergeInput extends IdSpan {
  readonly projectId: string;
  readonly status: BlockStatus;
  /** Block title (2026-10b §5.1); absent or null means "no title". */
  readonly title?: string | null;
}

export interface MergedGroup {
  readonly memberIds: readonly string[];
  readonly startMin: number;
  readonly endMin: number;
  readonly projectId: string;
  readonly status: BlockStatus;
  /** Normalized shared title of the group, or null (2026-10b §5.1). */
  readonly title: string | null;
}

/**
 * Group consecutive blocks (touching end to start) that share project, status and normalized
 * title (2026-10b §5.1: adjacent same-project blocks with different titles never merge — a
 * titled block names one concrete matter; untitled blocks only merge with untitled ones), for
 * display only: storage keeps every member as its own record. Blocks must belong to one
 * personal date.
 */
export function mergeForDisplay(blocks: readonly MergeInput[]): MergedGroup[] {
  const sorted = [...blocks].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin || (a.id < b.id ? -1 : 1),
  );
  const groups: MergedGroup[] = [];
  for (const block of sorted) {
    const title = normalizeBlockTitle(block.title);
    const last = groups[groups.length - 1];
    if (
      last !== undefined &&
      last.projectId === block.projectId &&
      last.status === block.status &&
      last.title === title &&
      last.endMin === block.startMin
    ) {
      groups[groups.length - 1] = {
        ...last,
        memberIds: [...last.memberIds, block.id],
        endMin: block.endMin,
      };
    } else {
      groups.push({
        memberIds: [block.id],
        startMin: block.startMin,
        endMin: block.endMin,
        projectId: block.projectId,
        status: block.status,
        title,
      });
    }
  }
  return groups;
}
